/**
 * SYM-6196. How much text fits on a slide.
 *
 * The model is held to a budget and a person was not. The prompt says
 * "MAX_CHARS_PER_SLIDE = 47. The text on each slide must be at most
 * MAX_CHARS_PER_SLIDE characters, because that is what a viewer can read in
 * the time the slide is on screen" (`VideoAssistantService.php:100-113`, the
 * number from `video-generation.timing.max_slide_characters`). The wizard had
 * no limit at all, so a headline of 104 characters and a supporting line of 97
 * went straight through: the supporting line was cut off and drawn behind the
 * WANTED badge, and it pushed the headline out of the frame.
 *
 * Two things follow from the prompt's wording, and both are the point of this
 * file:
 *
 * - the budget is for the TEXT ON THE SLIDE, which is the headline and the
 *   supporting line together, not each on its own;
 * - it is a number the server owns. The wizard uses whatever the server sent
 *   with the draft and only falls back to 47 when it sent nothing, so there is
 *   never a second number being maintained in the browser.
 */

/**
 * Used only when the server did not say. Same value as the server's own
 * default in `config/video-generation.php`.
 */
export const FALLBACK_MAX_SLIDE_CHARACTERS = 47;

/** The budget for one slide's text, preferring what the server said. */
export function slideCharacterBudget(fromServer?: number | null): number {
  if (typeof fromServer === 'number' && Number.isFinite(fromServer)) {
    const rounded = Math.floor(fromServer);
    if (rounded > 0) return rounded;
  }
  return FALLBACK_MAX_SLIDE_CHARACTERS;
}

/** How much of a slide's budget the two lines together have used. */
export function slideTextUsed(
  headline: string | null | undefined,
  subtext: string | null | undefined
): number {
  return (headline ?? '').length + (subtext ?? '').length;
}

/** How much room is left on a slide. Never negative. */
export function slideTextRemaining(
  headline: string | null | undefined,
  subtext: string | null | undefined,
  budget: number
): number {
  return Math.max(0, budget - slideTextUsed(headline, subtext));
}

/**
 * What the line under BOTH boxes says.
 *
 * It used to sit under the supporting line and count down ("38 characters left
 * on this slide"), which read as if it belonged to that one box. It is a budget
 * for the slide, so it says so: what the two lines have used, out of what the
 * slide allows.
 */
export function slideTextSummary(
  headline: string | null | undefined,
  subtext: string | null | undefined,
  budget: number
): string {
  return `${slideTextUsed(headline, subtext)} / ${budget} characters used`;
}

export type SlideBudgetState = 'within' | 'full' | 'over';

export interface SlideBudgetMessage {
  state: SlideBudgetState;
  text: string;
}

/**
 * SYM-6207. What the line under the two boxes says, including the case the
 * customer did not cause.
 *
 * The budget is put to the model in its instructions and nothing checks the
 * answer against it, so a slide can arrive longer than the rule allows. The
 * customer then deletes one character and the box starts refusing the next
 * one, about text they never wrote. Steven's call: leave what the model wrote
 * alone, and say plainly that the slide is over the length -- rather than
 * letting the customer discover it by being blocked.
 */
export function slideBudgetMessage(
  headline: string | null | undefined,
  subtext: string | null | undefined,
  budget: number
): SlideBudgetMessage {
  const used = slideTextUsed(headline, subtext);
  const summary = `${used} / ${budget} characters used`;

  if (used > budget) {
    const over = used - budget;
    return {
      state: 'over',
      text: `${summary} — ${over} too many. This slide was written longer than a viewer can read it; shorten it to ${budget} and you can edit it freely again.`,
    };
  }

  if (used === budget) {
    return {
      state: 'full',
      text: `${summary} — this slide is full. A viewer only has time to read so much.`,
    };
  }

  return { state: 'within', text: summary };
}

/**
 * The most characters one of the two boxes may hold, given what the other one
 * already holds.
 *
 * Text the model wrote can arrive longer than the budget -- it is asked, not
 * forced. In that case the limit is what is already there, so nothing the
 * customer never typed is silently cut, and they can only shorten it.
 */
export function maxLengthForField(
  thisField: string | null | undefined,
  otherField: string | null | undefined,
  budget: number
): number {
  const mine = (thisField ?? '').length;
  const theirs = (otherField ?? '').length;

  return Math.max(mine, budget - theirs);
}
